import {useState, useCallback, useMemo, useRef} from 'react'
import {ActionMenu, Button, CounterLabel, Dialog, Tooltip, type SelectPanelItemInput as ItemInput} from '@primer/react'
import {useMediaQuery} from '@github-ui/use-media-query'
import {sendEvent} from '@github-ui/hydro-analytics'
import {Dialog as ExperimentalDialog} from '@primer/react/experimental'
import {BellIcon, BellSlashIcon, EyeIcon} from '@primer/octicons-react'
import {updateSettings} from './services/api'

import SubscriptionList from './components/SubscriptionList'
import ThreadList, {type ThreadType} from './components/ThreadList'
import {SubscriptionTypeValue, subscriptionLabelText, subscriptionTypeText} from './utils/subscriptions'
import styles from './NotificationsSubscriptionsMenu.module.css'
import type {LabelPickerLabel} from '@github-ui/item-picker-label-tsq'

export type LabelType = {
  id: number
  name: string
  nameHTML: string
  color: string
  description?: string
  subscribed: boolean
}

export interface NotificationsSubscriptionsMenuProps {
  repositoryId: string
  repositoryName: string
  watchersCount: number
  subscriptionType: string
  subscribableThreadTypes: ThreadType[]
  showLabelSubscriptions: boolean
  subscribedLabels: LabelPickerLabel[]
}

type NotificationsSubscriptionsMenuComponentProps = NotificationsSubscriptionsMenuProps & {
  isLoggedIn?: boolean
  repoUrl?: string
  size?: 'small' | 'medium'
}

export function NotificationsSubscriptionsMenu({
  isLoggedIn = true,
  repoUrl,
  size = 'small',
  ...subscriptionProps
}: NotificationsSubscriptionsMenuComponentProps) {
  if (!isLoggedIn) {
    const description = 'You must be signed in to change notification settings'
    const loginHref = `/login?return_to=${encodeURIComponent(repoUrl ?? `/${subscriptionProps.repositoryName}`)}`

    return (
      <Tooltip text={description} type="description" direction="s">
        <Button
          as="a"
          href={loginHref}
          rel="nofollow"
          size={size}
          leadingVisual={BellIcon}
          aria-label={description}
          data-testid="notifications-sign-in-button"
        >
          Notifications
        </Button>
      </Tooltip>
    )
  }

  return <AuthenticatedNotificationsSubscriptionsMenu {...subscriptionProps} />
}

function AuthenticatedNotificationsSubscriptionsMenu({
  repositoryId,
  repositoryName,
  watchersCount,
  subscriptionType,
  subscribableThreadTypes,
  showLabelSubscriptions,
  subscribedLabels,
}: NotificationsSubscriptionsMenuProps) {
  const hasSubscribedLabels = subscribedLabels.length > 0

  const subscribedThreads = useMemo(() => {
    return subscribableThreadTypes
      .map(thread =>
        thread.subscribed || (thread.name === 'Issue' && showLabelSubscriptions && hasSubscribedLabels)
          ? thread.name
          : null,
      )
      .filter(t => t !== null)
  }, [subscribableThreadTypes, showLabelSubscriptions, hasSubscribedLabels])

  const [showError, setShowError] = useState(false)

  // dialog
  const [isOpen, setIsOpen] = useState(false)

  const [showSubscriptionError, setShowSubscriptionError] = useState(false)
  const [subscriptionError, setSubscriptionError] = useState<string>('')
  const onSubscriptionErrorDialogClose = useCallback(() => setShowSubscriptionError(false), [])

  // Subscription menu states
  const [selectedSubscription, setSelectedSubscription] = useState(
    subscribedThreads.length > 0 ? SubscriptionTypeValue.CUSTOM : subscriptionType,
  )
  const [previousSubscription, setPreviousSubscription] = useState<string>(selectedSubscription)

  // Thread menu states
  const [appliedThreads, setAppliedThreads] = useState<string[]>(subscribedThreads)
  const [appliedLabels, setAppliedLabels] = useState<ItemInput[] | LabelPickerLabel[]>(subscribedLabels)
  const [isSavingThreads, setIsSavingThreads] = useState<boolean>(false)

  const anchorRef = useRef<HTMLButtonElement>(null)

  const onCloseDialog = useCallback(() => {
    setIsOpen(false)
    setSelectedSubscription(previousSubscription)
  }, [previousSubscription])

  const onCancelCustomMenu = onCloseDialog

  const saveThreads = useCallback(
    async (selectedThreads: string[], selectedRepoLabels: ItemInput[] | LabelPickerLabel[]) => {
      setIsSavingThreads(true)
      setAppliedThreads(selectedThreads)
      setAppliedLabels(selectedRepoLabels)
      setPreviousSubscription(SubscriptionTypeValue.CUSTOM)

      const formData = new FormData()
      formData.set('do', 'custom')
      formData.set('repository_id', repositoryId)
      // save thread types
      selectedThreads.map(threadName => {
        formData.append(`thread_types[]`, threadName)
      })
      // save labels
      selectedRepoLabels.map(label => {
        if (label.id) {
          formData.append(`labels[]`, label.id.toString())
        }
      })

      sendEvent('notifications.watching_settings.update', {
        subscriptionState: 'custom',
        selectedThreads: selectedThreads.join(','),
        selectedRepoLabelsCount: selectedRepoLabels.length,
        repositoryId,
      })

      const response = await updateSettings(formData)
      if (response.ok) {
        setIsOpen(false)
        setIsSavingThreads(false)
      } else {
        setShowError(true)
      }
    },
    [repositoryId],
  )

  const saveData = useCallback(
    async (option: string) => {
      const formData = new FormData()
      if (option === SubscriptionTypeValue.IGNORING) {
        formData.set('do', 'ignore')
      } else if (option === SubscriptionTypeValue.WATCHING) {
        formData.set('do', 'subscribed')
      } else if (
        option === SubscriptionTypeValue.NONE ||
        (option === SubscriptionTypeValue.CUSTOM && appliedThreads.length === 0)
      ) {
        formData.set('do', 'included')
      }

      formData.append(`thread_types[]`, '')

      formData.set('repository_id', repositoryId)

      return await updateSettings(formData)
    },
    [repositoryId, appliedThreads],
  )

  // Select menu option: Participating and @mentions, All Activity, Ignore, Custom
  const selectMenuOption = useCallback(
    async (option: string) => {
      sendEvent('notifications.watching_settings.update', {
        subscriptionState: option,
        repositoryId,
      })
      if (option === SubscriptionTypeValue.CUSTOM) {
        setIsOpen(true)
        setSelectedSubscription(SubscriptionTypeValue.CUSTOM)
      } else {
        const result = await saveData(option)
        if (result.ok) {
          setSelectedSubscription(option)
          setPreviousSubscription(option)
          setAppliedThreads([])
        } else {
          // eslint-disable-next-line react-hooks/immutability
          handleSubscriptionError(result.errorMsg)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSelectedSubscription],
  )

  const handleSubscriptionError = (errorMsg: string) => {
    switch (errorMsg) {
      case 'limit_exceeded':
        setShowSubscriptionError(true)
        setSubscriptionError('limit_exceeded')
        break
      case 'forbidden':
        setShowSubscriptionError(true)
        setSubscriptionError('forbidden')
        break
      default:
      // we don't show an error here for now.
    }
  }

  const ariaLabel = useMemo(
    () => subscriptionLabelText(selectedSubscription, repositoryName, watchersCount),
    [selectedSubscription, repositoryName, watchersCount],
  )

  // Match the size of the fork and star buttons at different viewport sizes
  // Primer's narrow viewport range
  const isNarrow = useMediaQuery('(max-width: calc(48rem - 0.02px))')

  return (
    <>
      <ActionMenu>
        <ActionMenu.Button
          data-testid="notifications-subscriptions-menu-button"
          size={isNarrow ? 'medium' : 'small'}
          leadingVisual={selectedSubscription === SubscriptionTypeValue.IGNORING ? BellSlashIcon : EyeIcon}
          trailingVisual={<CounterLabel>{watchersCount}</CounterLabel>}
          className={styles.ActionMenuButton}
          aria-label={ariaLabel}
        >
          {subscriptionTypeText(selectedSubscription)}
        </ActionMenu.Button>
        <ActionMenu.Overlay width="medium">
          <SubscriptionList selected={selectedSubscription} onSelect={selectMenuOption} />
        </ActionMenu.Overlay>
      </ActionMenu>
      {isOpen && (
        <Dialog
          className={styles.subscriptionDialog}
          returnFocusRef={anchorRef}
          title={`Subscribe to events for ${repositoryName}`}
          onClose={() => onCloseDialog()}
        >
          <div data-testid="inner">
            <ThreadList
              subscribableThreadTypes={subscribableThreadTypes}
              showLabelSubscriptions={showLabelSubscriptions}
              cancelMenuCallback={onCancelCustomMenu}
              appliedThreads={appliedThreads}
              appliedLabels={appliedLabels}
              saveThreads={saveThreads}
              showError={showError}
              isSavingThreads={isSavingThreads}
              repo={repositoryName}
            />
          </div>
        </Dialog>
      )}
      {showSubscriptionError && (
        <ExperimentalDialog title="You are unable to watch this repository" onClose={onSubscriptionErrorDialogClose}>
          <ExperimentalDialog.Body>
            {subscriptionError === 'forbidden' && 'This action is not available for this repository.'}
            {subscriptionError === 'limit_exceeded' && (
              <>
                You have reached the limit of 10,000 watched repositories. Update your{' '}
                <a href="/watching">watch settings</a> to continue.
              </>
            )}
          </ExperimentalDialog.Body>
        </ExperimentalDialog>
      )}
    </>
  )
}
